import { useState } from "react";
import { X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { AronApiBaseContext } from "@/features/aron/api/aronApi";
import { AronChatView } from "@/features/aron/components/AronChatView";
import { AronConversationList } from "@/features/aron/components/AronConversationList";
import { useAronChat } from "@/features/aron/hooks/useAronChat";

/**
 * DLC_04 / ENG-ARON — czat ze sprawdzającym. Reużywa CAŁĄ maszynerię czatu ARONa
 * (komponenty, hooki, streaming Centrifugo) pod bazą API "/eng-aron" — backend
 * przypina personę "eng" (własna baza wiedzy projektowej, zero danych ELG5).
 */
function EngAronChatInner() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("c");

  const select = (id: string | null) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set("c", id);
        else next.delete("c");
        return next;
      },
      { replace: true },
    );
  };

  const { sending, handleSend, handleStop } = useAronChat(selectedId, select);
  const [listOpen, setListOpen] = useState(false);

  return (
    <div className="-mx-4 -my-4 lg:-mx-6 lg:-my-6 flex h-[calc(100dvh-3.5rem)] overflow-hidden">
      <aside className="hidden w-64 shrink-0 border-r border-gray-200 md:flex md:flex-col dark:border-gray-700">
        <AronConversationList
          selectedId={selectedId}
          onSelect={(id) => select(id)}
          onNew={() => select(null)}
        />
      </aside>

      {listOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setListOpen(false)} />
          <div className="absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col bg-white shadow-xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-700">
              <span className="text-sm font-medium">Rozmowy</span>
              <button
                type="button"
                onClick={() => setListOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <AronConversationList
                selectedId={selectedId}
                onSelect={(id) => {
                  select(id);
                  setListOpen(false);
                }}
                onNew={() => {
                  select(null);
                  setListOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <main className="flex min-w-0 flex-1 flex-col">
        <AronChatView
          key={selectedId ?? "new"}
          conversationId={selectedId}
          sending={sending}
          onSend={handleSend}
          onStop={handleStop}
          onOpenList={() => setListOpen(true)}
        />
      </main>
    </div>
  );
}

export function EngAronChatPage() {
  return (
    <AronApiBaseContext.Provider value="/eng-aron">
      <EngAronChatInner />
    </AronApiBaseContext.Provider>
  );
}
